import styles from "./Badge.module.css";

/**
 * Badge semantics are canon (`contracts/DESIGN-TOKENS.md`); they are not interchangeable
 * decoration:
 *
 *   ltd        deal-type signal — genuine lifetime access under the disclosed terms
 *   exclusive  availability signal — exclusive to Dealbid for the represented period
 *   founding   historical community signal — one of the first 10 approved deals
 *   verified   review-status signal — seller association and deal detail were checked
 *
 * The canvas gives each a distinct *shape*, not just a colour, and that is what keeps canon's
 * "status is never conveyed through colour alone" true here: LTD is a filled accent pill,
 * Exclusive is an outlined pill, Founding Deal is plain text on a dotted underline, and
 * Verified is a green check and word with no pill at all. Every one also carries its name in
 * words, so nothing depends on telling the shapes apart either.
 *
 * The earlier build wrapped all four in identical bordered pills with SVG glyphs. It was
 * legible but it was not this design, and four identical chips per row is most of what made
 * the board look busy against the canvas.
 *
 * The wording is canon's, not the canvas's, where the two differ: the canvas shortens the
 * review-status badge to "Verified" and `CANON.md` § 3 calls it **Deal Verified**, which
 * outranks a design contract on what a thing is named. The canvas's *treatment* — a check
 * and a word in the positive colour, no pill — is what carries over.
 */
export type BadgeKind = "ltd" | "exclusive" | "founding" | "verified" | "sponsored";

const LABELS: Record<BadgeKind, string> = {
  ltd: "LTD",
  exclusive: "Exclusive",
  founding: "Founding Deal",
  verified: "Deal Verified",
  sponsored: "Sponsored",
};

export function Badge({ kind, label }: { kind: BadgeKind; label?: string | undefined }) {
  return (
    /*
     * The check and the word share one element rather than nesting the mark in its own
     * span: a badge whose text is split across elements cannot be found by its text, which
     * is how both a test and a screen-reader user look for it.
     */
    <span className={`${styles.badge} ${styles[kind]}`}>
      {kind === "verified" ? "✓ " : null}
      {label ?? LABELS[kind]}
    </span>
  );
}
